/**
 * Minimal Zustand-compatible store factory.
 * Eliminates external zustand dependency while providing the same API:
 * - createStore<T>((set, get) => initialState) → useStore hook
 * - useStore() returns full state, triggers re-render on changes
 * - useStore.getState() / useStore.subscribe() for external access
 *
 * FIX ML-5 + SRE-R5-1: Selector is stored in a ref so inline arrows
 * (e.g. `useStore(s => s.foo)`) do NOT cause subscribe/unsubscribe
 * churn on every render. The subscription is created once on mount.
 */

import { useState, useEffect, useRef } from 'react';
export function createStore<T extends object>(initializer: (set: (partial: Partial<T> | ((state: T) => Partial<T>)) => void, get: () => T) => T) {
  let state: T;
  const listeners = new Set<() => void>();
  const get = () => state;
  const set = (partial: Partial<T> | ((s: T) => Partial<T>)) => {
    const nextPartial = typeof partial === 'function' ? partial(state) : partial;
    const nextState = {
      ...state,
      ...nextPartial
    };
    if (!Object.is(state, nextState)) {
      state = nextState;
      listeners.forEach(l => l());
    }
  };
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };
  state = initializer(set, get);

  // React hook — universally compatible (React 16+)
  function useStore(): T;
  function useStore<U>(selector: (state: T) => U): U;
  function useStore<U>(selector?: (state: T) => U): T | U {
    // Store selector in a ref so inline arrows don't cause effect re-runs.
    // This eliminates ~55 subscribe/unsubscribe cycles per render in useAppStore.
    const selectorRef = useRef(selector);
    selectorRef.current = selector;
    const getSelection = () => {
      const sel = selectorRef.current;
      return sel ? sel(get()) : get();
    };
    const [selection, setSelection] = useState(getSelection);
    useEffect(() => {
      // Catch edge case where state changed between render and effect
      // CRITICAL: Must wrap in arrow to prevent React from calling function
      // values as state updaters (e.g. when selector returns a function
      // like createAccount, React would call it with prev state as args).
      setSelection(() => getSelection());
      return subscribe(() => {
        const nextSelection = getSelection();
        setSelection(prev => {
          if (Object.is(prev, nextSelection)) return prev;
          return nextSelection;
        });
      });
    }, []); // Empty deps — subscribe once, selector read from ref

    return selection;
  }
  useStore.getState = get;
  useStore.setState = set;
  useStore.subscribe = (cb: (state: T, prevState: T) => void) => {
    let prev = state;
    return subscribe(() => {
      const next = state;
      cb(next, prev);
      prev = next;
    });
  };
  return useStore;
}